import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormHelperText } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { response } from '../../../api/db';
import { sendMailForResisterUser } from '../../../api/db/user';
import { userRegisterSchema } from '../../../validation/user';
import type { UserRegisterType } from '../../../validation/user/userType';
import {
  black,
  linkColor,
  primaryButtonBackgroundColor,
  primaryColor,
  primaryLinkColor
} from '../../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  headerHeight,
  spHeaderHeight,
  textMb,
  titleMb,
  w100
} from '../../../constants/Location';
import { common } from '../../../modules/error';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';
import CustomHead from '../CustomHead';
import { AuthContext } from '../../../contexts/auth';
import { sentryLog } from '../../../config/sentry';

const styles = {
  CheckboxM: {
    '@media screen and (max-width:600px)': {
      marginLeft: 0
    },
    marginBottom: '40px'
  },
  cardContentPadding,
  cardSize,
  container: {
    '@media screen and (max-width:600px)': {
      mt: spHeaderHeight
    },
    mt: headerHeight
  },
  loginLink: {
    marginTop: '24px',
    ...primaryLinkColor
  },
  required: {
    color: primaryColor,
    marginLeft: '10px'
  },
  textMb,
  textSmall: {
    fontSize: '11.5px'
  },
  titleMb,
  fontBold: {
    fontSize: '32px',
    fontWeight: 'bold',
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    }
  },
  w100
};

const AuthRegister: React.FC = () => {
  const { isLoggedIn } = useContext(AuthContext);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/home');
    }
  }, [isLoggedIn]);

  const { t } = useTranslation();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<UserRegisterType>({
    defaultValues: {
      privacyCheck: false
    },
    resolver: yupResolver(userRegisterSchema)
  });

  const checkboxHelper = (): string | undefined => {
    if (errors.privacyCheck === undefined) return;
    return String(errors.privacyCheck.message);
  };

  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('privacyCheck') === undefined ||
        false ||
        Boolean(errors.email) ||
        Boolean(errors.privacyCheck)
      );
    }
    return false;
  };

  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await sendMailForResisterUser(watch('email'));
      navigate('/auth/register/sent');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const nextJsBaseUrl = process.env.REACT_APP_NEXT_JS_BASE_URL;

  return (
    <>
      <CustomHead title="会員登録 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={styles.container}
      >
        <Grid item>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, styles.fontBold, { color: black }]}
                component="div"
              >
                {t('register.title')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText={t('form.email')}
                    requiredText={t('form.required')}
                    placeholder="xxxxx"
                  />
                )}
                control={control}
                name="email"
              />
              <Typography sx={styles.CheckboxM} component="div">
                <Controller
                  render={({ field }) => (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            sx={{
                              color: black,
                              '&.Mui-checked': {
                                color: primaryColor
                              }
                            }}
                          />
                        }
                        sx={{ marginLeft: 0 }}
                        label={
                          <>
                            <Link
                              sx={linkColor}
                              target="_blank"
                              href={nextJsBaseUrl + '/term'}
                            >
                              {t('form.term')}
                            </Link>
                            <Typography
                              component="span"
                              color={black}
                              sx={styles.textSmall}
                            >
                              、
                            </Typography>
                            <Link
                              sx={linkColor}
                              target="_blank"
                              href={'https://www.tokyu-cnst.co.jp/privacy'}
                            >
                              {t('form.privacy')}
                            </Link>
                            <Typography
                              component="span"
                              color={black}
                              sx={styles.textSmall}
                            >
                              {t('form.agree')}
                            </Typography>
                          </>
                        }
                      />
                      <FormHelperText error={Boolean(errors.privacyCheck)}>
                        {checkboxHelper()}
                      </FormHelperText>
                    </FormGroup>
                  )}
                  control={control}
                  name="privacyCheck"
                />
              </Typography>
              <CardActions sx={[styles.textMb, { p: 0 }]}>
                <Grid container direction="column" alignItems="center">
                  <Button
                    disabled={buttonDisabledFlag()}
                    variant="contained"
                    fullWidth
                    sx={[primaryButtonBackgroundColor, { height: '44px' }]}
                    onClick={() => {
                      void (async () => {
                        setFirstSubmit(true);
                        await handleSubmit(async () => await onSubmit())();
                      })();
                    }}
                  >
                    <Typography
                      component="div"
                      color="white"
                      sx={{ fontSize: '16px' }}
                    >
                      {t('register.emailAuth')}
                    </Typography>
                  </Button>
                  <Link href={'/auth/login'} sx={styles.loginLink}>
                    既に会員登録済の方はこちら
                  </Link>
                </Grid>
              </CardActions>
            </CardContent>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AuthRegister;
